import { useEffect, useRef, useState } from 'react'
import { Radio, Satellite, Compass } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SpaceGlobeProps {
  className?: string
}

interface SatelliteData {
  id: string
  name: string
  code: string
  inclination: number
  radiusFactor: number
  speed: number
  phase: number
  altitude: string
  status: string
  accent: string
}

const SATELLITES: SatelliteData[] = [
  {
    id: 'sat-alpha',
    name: 'UPBO-SAT α',
    code: 'POLAR-01',
    inclination: (58 * Math.PI) / 180,
    radiusFactor: 1.38,
    speed: 0.009,
    phase: 0.4,
    altitude: '480 km',
    status: 'ACTIVE • 100%',
    accent: '#38bdf8', // radiant celestial cyan
  },
  {
    id: 'sat-beta',
    name: 'UPBO-SAT β',
    code: 'GEO-02',
    inclination: (-28 * Math.PI) / 180,
    radiusFactor: 1.62,
    speed: 0.006,
    phase: 2.8,
    altitude: '1,240 km',
    status: 'SYNCED • 8ms',
    accent: '#fbbf24', // champagne gold
  },
  {
    id: 'sat-gamma',
    name: 'UPBO-SAT γ',
    code: 'MESH-03',
    inclination: (78 * Math.PI) / 180,
    radiusFactor: 1.48,
    speed: -0.008,
    phase: 4.6,
    altitude: '650 km',
    status: 'RELAY • 60 FPS',
    accent: '#34d399', // emerald aurora
  },
]

// Landmass cluster seeds (latitudes -90..90, longitudes -180..180)
const CONTINENT_CLUSTERS: Array<{ lat: number; lon: number; spread: number; count: number }> = [
  // North America
  { lat: 45, lon: -100, spread: 22, count: 28 },
  { lat: 35, lon: -85, spread: 18, count: 22 },
  { lat: 55, lon: -115, spread: 20, count: 18 },
  // South America
  { lat: -12, lon: -55, spread: 20, count: 30 },
  { lat: -30, lon: -60, spread: 16, count: 18 },
  // Europe
  { lat: 52, lon: 15, spread: 16, count: 26 },
  { lat: 44, lon: 8, spread: 14, count: 22 },
  // Africa
  { lat: 5, lon: 20, spread: 24, count: 35 },
  { lat: -20, lon: 25, spread: 18, count: 25 },
  // Asia
  { lat: 38, lon: 75, spread: 26, count: 40 },
  { lat: 55, lon: 95, spread: 24, count: 30 },
  { lat: 25, lon: 105, spread: 20, count: 32 },
  { lat: 15, lon: 102, spread: 16, count: 24 }, // Southeast Asia
  { lat: 36, lon: 138, spread: 10, count: 16 }, // Japan / East Asia
  // Australia
  { lat: -25, lon: 135, spread: 18, count: 26 },
]

function generateContinentPoints() {
  const points: Array<{ lat: number; lon: number; r: number }> = []
  let seed = 1337
  const random = () => {
    seed = (seed * 16807) % 2147483647
    return (seed - 1) / 2147483646
  }

  for (const cluster of CONTINENT_CLUSTERS) {
    for (let i = 0; i < cluster.count; i++) {
      const u = random()
      const v = random()
      const w = Math.sqrt(-2 * Math.log(u || 0.001))
      const dLat = w * Math.cos(2 * Math.PI * v) * (cluster.spread * 0.45)
      const dLon = w * Math.sin(2 * Math.PI * v) * (cluster.spread * 0.55)
      const lat = Math.max(-85, Math.min(85, cluster.lat + dLat))
      const lon = ((cluster.lon + dLon + 540) % 360) - 180
      points.push({ lat, lon, r: 1.2 + random() * 1.4 })
    }
  }
  return points
}

const CONTINENT_POINTS = generateContinentPoints()

export function SpaceGlobe({ className }: SpaceGlobeProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [activeSat, setActiveSat] = useState<SatelliteData>(SATELLITES[0])
  const [telemetryLatency, setTelemetryLatency] = useState(8)

  // Drag & Inertia state
  const rotX = useRef(0.24)
  const rotY = useRef(0)
  const velX = useRef(0)
  const velY = useRef(0.0035)
  const isDragging = useRef(false)
  const lastMousePos = useRef({ x: 0, y: 0 })

  useEffect(() => {
    const interval = setInterval(() => {
      setTelemetryLatency(Math.floor(7 + Math.random() * 4))
    }, 2400)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let animId: number
    const startTime = performance.now()

    // Generate static stars once
    const starCount = 90
    const stars: Array<{ x: number; y: number; size: number; alpha: number; speed: number }> = []
    for (let i = 0; i < starCount; i++) {
      stars.push({
        x: Math.random(),
        y: Math.random(),
        size: 0.6 + Math.random() * 1.4,
        alpha: 0.2 + Math.random() * 0.7,
        speed: 0.5 + Math.random() * 1.5,
      })
    }

    // Satellite trailing buffers
    const satTrails: Record<string, Array<{ x: number; y: number; z: number }>> = {
      'sat-alpha': [],
      'sat-beta': [],
      'sat-gamma': [],
    }

    const render = (time: number) => {
      const elapsed = (time - startTime) * 0.001
      const rect = canvas.getBoundingClientRect()
      const width = rect.width
      const height = rect.height

      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      if (canvas.width !== Math.floor(width * dpr) || canvas.height !== Math.floor(height * dpr)) {
        canvas.width = Math.floor(width * dpr)
        canvas.height = Math.floor(height * dpr)
      }

      ctx.save()
      ctx.scale(dpr, dpr)
      ctx.clearRect(0, 0, width, height)

      // Handle dragging inertia
      if (!isDragging.current) {
        rotX.current += velX.current
        rotY.current += velY.current
        velX.current *= 0.94
        velY.current = velY.current * 0.94 + 0.0035 * 0.06 // recover steady auto-spin
        rotX.current = Math.max(-0.85, Math.min(0.85, rotX.current))
      }

      const cx = width * 0.5
      const cy = height * 0.52
      const R = Math.min(width, height) * 0.36

      // ── 1. Twinkling Background Deep-Space Stars ─────────────────────────
      for (let i = 0; i < stars.length; i++) {
        const star = stars[i]
        const pulse = 0.5 + 0.5 * Math.sin(elapsed * star.speed + i)
        const alpha = star.alpha * pulse
        ctx.fillStyle = `rgba(224, 242, 254, ${alpha.toFixed(2)})`
        ctx.beginPath()
        ctx.arc(star.x * width, star.y * height, star.size, 0, Math.PI * 2)
        ctx.fill()
      }

      // ── 2. Atmospheric Nebula & Outer Rayleigh Scattering Aura ───────────
      const auraGrad = ctx.createRadialGradient(cx, cy, R * 0.88, cx, cy, R * 1.34)
      auraGrad.addColorStop(0, 'rgba(56, 189, 248, 0.22)')
      auraGrad.addColorStop(0.35, 'rgba(56, 189, 248, 0.09)')
      auraGrad.addColorStop(0.7, 'rgba(30, 64, 175, 0.04)')
      auraGrad.addColorStop(1, 'rgba(15, 23, 42, 0)')
      ctx.fillStyle = auraGrad
      ctx.beginPath()
      ctx.arc(cx, cy, R * 1.34, 0, Math.PI * 2)
      ctx.fill()

      // ── 3. 3D Globe Base Sphere (Dark Planetary Terminator) ──────────────
      const sphereGrad = ctx.createRadialGradient(
        cx - R * 0.35,
        cy - R * 0.35,
        R * 0.05,
        cx,
        cy,
        R
      )
      sphereGrad.addColorStop(0, 'rgba(14, 28, 48, 0.98)')
      sphereGrad.addColorStop(0.55, 'rgba(8, 16, 28, 0.99)')
      sphereGrad.addColorStop(1, 'rgba(2, 6, 14, 1)')
      ctx.fillStyle = sphereGrad
      ctx.beginPath()
      ctx.arc(cx, cy, R, 0, Math.PI * 2)
      ctx.fill()

      // Globe subtle rim border
      ctx.strokeStyle = 'rgba(56, 189, 248, 0.28)'
      ctx.lineWidth = 1.2
      ctx.stroke()

      // Projection helper
      const project = (latDeg: number, lonDeg: number, radius = R) => {
        const phi = (90 - latDeg) * (Math.PI / 180)
        const theta = (lonDeg + 180) * (Math.PI / 180)

        const x0 = -(radius * Math.sin(phi) * Math.cos(theta))
        const y0 = radius * Math.cos(phi)
        const z0 = radius * Math.sin(phi) * Math.sin(theta)

        const cosY = Math.cos(rotY.current)
        const sinY = Math.sin(rotY.current)
        const x1 = x0 * cosY + z0 * sinY
        const z1 = -x0 * sinY + z0 * cosY

        const cosX = Math.cos(rotX.current)
        const sinX = Math.sin(rotX.current)
        const y2 = y0 * cosX - z1 * sinX
        const z2 = y0 * sinX + z1 * cosX

        const d = 800
        const prjScale = d / (d - z2)
        return {
          x: cx + x1 * prjScale,
          y: cy + y2 * prjScale,
          z: z2,
          prjScale,
        }
      }

      // ── 4. 3D Latitude & Longitude Coordinate Grid ────────────────────────
      const drawParallels = () => {
        const lats = [-60, -30, 0, 30, 60]
        ctx.lineWidth = 0.8
        for (const lat of lats) {
          ctx.beginPath()
          let first = true
          for (let lon = -180; lon <= 180; lon += 6) {
            const pt = project(lat, lon, R * 0.998)
            if (pt.z > 0) {
              if (first) {
                ctx.moveTo(pt.x, pt.y)
                first = false
              } else {
                ctx.lineTo(pt.x, pt.y)
              }
            } else {
              first = true
            }
          }
          ctx.strokeStyle = lat === 0 ? 'rgba(56, 189, 248, 0.32)' : 'rgba(56, 189, 248, 0.12)'
          ctx.stroke()
        }
      }

      const drawMeridians = () => {
        ctx.lineWidth = 0.7
        ctx.strokeStyle = 'rgba(56, 189, 248, 0.12)'
        for (let lon = -180; lon < 180; lon += 30) {
          ctx.beginPath()
          let first = true
          for (let lat = -80; lat <= 80; lat += 5) {
            const pt = project(lat, lon, R * 0.998)
            if (pt.z > 0) {
              if (first) {
                ctx.moveTo(pt.x, pt.y)
                first = false
              } else {
                ctx.lineTo(pt.x, pt.y)
              }
            } else {
              first = true
            }
          }
          ctx.stroke()
        }
      }

      drawParallels()
      drawMeridians()

      // ── 5. Landmass Dot Matrix (Continents) with Atmospheric Fresnel ──────
      for (let i = 0; i < CONTINENT_POINTS.length; i++) {
        const cp = CONTINENT_POINTS[i]
        const pt = project(cp.lat, cp.lon, R)
        if (pt.z > -R * 0.05) {
          const depthAlpha = Math.max(0.08, (pt.z + R * 0.05) / (R * 1.05))
          const distFromCenter = Math.hypot(pt.x - cx, pt.y - cy) / R
          const limbFresnel = Math.max(0.15, Math.min(1, Math.sqrt(Math.max(0, 1.02 - distFromCenter))))
          const alpha = depthAlpha * limbFresnel * 0.88

          ctx.fillStyle = `rgba(103, 232, 249, ${alpha.toFixed(2)})`
          ctx.beginPath()
          ctx.arc(pt.x, pt.y, cp.r * pt.prjScale, 0, Math.PI * 2)
          ctx.fill()
        }
      }

      // ── 6. Orbiting Satellites in 3D Space ─────────────────────────────────
      for (const sat of SATELLITES) {
        const orbitRadius = R * sat.radiusFactor
        const currentAngle = elapsed * sat.speed + sat.phase

        // Orbit dashed path curve
        ctx.beginPath()
        const stepCount = 64
        for (let s = 0; s <= stepCount; s++) {
          const th = (s / stepCount) * Math.PI * 2
          const ox0 = orbitRadius * Math.cos(th)
          const oy0 = orbitRadius * Math.sin(th) * Math.sin(sat.inclination)
          const oz0 = orbitRadius * Math.sin(th) * Math.cos(sat.inclination)

          const cosY = Math.cos(rotY.current * 0.25)
          const sinY = Math.sin(rotY.current * 0.25)
          const ox1 = ox0 * cosY + oz0 * sinY
          const oz1 = -ox0 * sinY + oz0 * cosY

          const cosX = Math.cos(rotX.current)
          const sinX = Math.sin(rotX.current)
          const oy2 = oy0 * cosX - oz1 * sinX
          const oz2 = oy0 * sinX + oz1 * cosX

          const orbitScale = 800 / (800 - oz2)
          const px = cx + ox1 * orbitScale
          const py = cy + oy2 * orbitScale

          if (s === 0) ctx.moveTo(px, py)
          else ctx.lineTo(px, py)
        }
        ctx.strokeStyle = `${sat.accent}25`
        ctx.lineWidth = 1
        ctx.setLineDash([4, 6])
        ctx.stroke()
        ctx.setLineDash([])

        // Satellite 3D coordinates
        const sx0 = orbitRadius * Math.cos(currentAngle)
        const sy0 = orbitRadius * Math.sin(currentAngle) * Math.sin(sat.inclination)
        const sz0 = orbitRadius * Math.sin(currentAngle) * Math.cos(sat.inclination)

        const cosY = Math.cos(rotY.current * 0.25)
        const sinY = Math.sin(rotY.current * 0.25)
        const sx1 = sx0 * cosY + sz0 * sinY
        const sz1 = -sx0 * sinY + sz0 * cosY

        const cosX = Math.cos(rotX.current)
        const sinX = Math.sin(rotX.current)
        const sy2 = sy0 * cosX - sz1 * sinX
        const sz2 = sy0 * sinX + sz1 * cosX

        const satScale = 800 / (800 - sz2)
        const satScreenX = cx + sx1 * satScale
        const satScreenY = cy + sy2 * satScale

        const distFromPlanetCenter = Math.hypot(satScreenX - cx, satScreenY - cy)
        const isBehind = sz2 < -R * 0.1 && distFromPlanetCenter < R * 0.98

        const trail = satTrails[sat.id]
        trail.push({ x: satScreenX, y: satScreenY, z: sz2 })
        if (trail.length > 24) trail.shift()

        if (!isBehind && trail.length > 1) {
          ctx.beginPath()
          ctx.moveTo(trail[0].x, trail[0].y)
          for (let t = 1; t < trail.length; t++) {
            ctx.lineTo(trail[t].x, trail[t].y)
          }
          ctx.strokeStyle = `${sat.accent}45`
          ctx.lineWidth = 1.4
          ctx.stroke()
        }

        if (!isBehind) {
          // Radar pulse ring
          const pulsePhase = (elapsed * 2.5 + sat.phase) % 1
          const pulseR = 5 + pulsePhase * 16
          ctx.strokeStyle = `${sat.accent}${Math.floor((1 - pulsePhase) * 180).toString(16).padStart(2, '0')}`
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.arc(satScreenX, satScreenY, pulseR, 0, Math.PI * 2)
          ctx.stroke()

          // Ground laser communication link
          const groundPt = project(
            Math.sin(currentAngle) * 45,
            Math.cos(currentAngle) * 180,
            R
          )
          if (groundPt.z > 0) {
            ctx.beginPath()
            ctx.moveTo(satScreenX, satScreenY)
            ctx.lineTo(groundPt.x, groundPt.y)
            ctx.strokeStyle = `${sat.accent}25`
            ctx.lineWidth = 0.8
            ctx.setLineDash([2, 4])
            ctx.stroke()
            ctx.setLineDash([])

            ctx.fillStyle = sat.accent
            ctx.beginPath()
            ctx.arc(groundPt.x, groundPt.y, 2.5, 0, Math.PI * 2)
            ctx.fill()
          }

          // Satellite chassis
          ctx.save()
          ctx.translate(satScreenX, satScreenY)
          ctx.rotate(currentAngle + Math.PI / 4)

          // Solar wings
          ctx.fillStyle = 'rgba(56, 189, 248, 0.85)'
          ctx.fillRect(-8, -1.8, 4.5, 3.6)
          ctx.fillRect(3.5, -1.8, 4.5, 3.6)

          // Core node
          ctx.fillStyle = sat.accent
          ctx.beginPath()
          ctx.arc(0, 0, 3, 0, Math.PI * 2)
          ctx.fill()

          ctx.restore()

          // Active satellite telemetry badge
          if (sat.id === 'sat-alpha' || sat.id === activeSat.id) {
            ctx.font = '500 10px "JetBrains Mono", monospace'
            const labelText = `${sat.name} [${sat.altitude}]`
            const textMetrics = ctx.measureText(labelText)
            const boxW = textMetrics.width + 14
            const boxH = 18
            const boxX = satScreenX + 10
            const boxY = satScreenY - 10

            ctx.fillStyle = 'rgba(8, 16, 28, 0.85)'
            ctx.strokeStyle = `${sat.accent}60`
            ctx.lineWidth = 0.8
            ctx.beginPath()
            ctx.roundRect(boxX, boxY, boxW, boxH, 4)
            ctx.fill()
            ctx.stroke()

            ctx.fillStyle = sat.accent
            ctx.fillText(labelText, boxX + 7, boxY + 12.5)
          }
        }
      }

      // ── 7. Atmospheric Solar Limb Crescent Glow ──────────────────────────
      const limbGrad = ctx.createLinearGradient(cx - R, cy - R, cx, cy)
      limbGrad.addColorStop(0, 'rgba(103, 232, 249, 0.75)')
      limbGrad.addColorStop(0.3, 'rgba(56, 189, 248, 0.35)')
      limbGrad.addColorStop(0.7, 'rgba(56, 189, 248, 0.05)')
      limbGrad.addColorStop(1, 'rgba(0, 0, 0, 0)')

      ctx.save()
      ctx.beginPath()
      ctx.arc(cx, cy, R, 0, Math.PI * 2)
      ctx.clip()

      ctx.strokeStyle = limbGrad
      ctx.lineWidth = 6
      ctx.beginPath()
      ctx.arc(cx, cy, R - 1, Math.PI * 0.85, Math.PI * 1.85)
      ctx.stroke()
      ctx.restore()

      ctx.restore()
      animId = requestAnimationFrame(render)
    }

    animId = requestAnimationFrame(render)
    return () => cancelAnimationFrame(animId)
  }, [activeSat])

  // Mouse / Touch handlers for manual rotation
  const handlePointerDown = (e: React.PointerEvent) => {
    isDragging.current = true
    lastMousePos.current = { x: e.clientX, y: e.clientY }
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current) return
    const dx = e.clientX - lastMousePos.current.x
    const dy = e.clientY - lastMousePos.current.y
    lastMousePos.current = { x: e.clientX, y: e.clientY }

    rotY.current += dx * 0.007
    rotX.current += dy * 0.007
    velY.current = dx * 0.0035
    velX.current = dy * 0.0035
  }

  const handlePointerUp = (e: React.PointerEvent) => {
    isDragging.current = false
    try {
      ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
    } catch {
      // ignore
    }
  }

  return (
    <div data-upbo-src="src/components/SpaceGlobe.tsx:535"
      ref={containerRef}
      className={cn(
        'relative rounded-2xl sm:rounded-3xl border border-sky-500/20 bg-card/60 backdrop-blur-xl overflow-hidden shadow-2xl p-4 sm:p-5 select-none',
        className
      )}
    >
      {/* Top Telemetry Header Bar */}
      <div data-upbo-src="src/components/SpaceGlobe.tsx:543" className="flex items-center justify-between gap-2 border-b border-sky-500/15 pb-3">
        <div data-upbo-src="src/components/SpaceGlobe.tsx:544" className="flex items-center gap-2">
          <div data-upbo-src="src/components/SpaceGlobe.tsx:545" className="size-2 rounded-full bg-cyan-400 animate-ping" />
          <span data-upbo-src="src/components/SpaceGlobe.tsx:546" className="font-mono text-[11px] sm:text-xs uppercase tracking-wider text-cyan-400 font-medium">
            Global Orbital Grid
          </span>
        </div>
        <div data-upbo-src="src/components/SpaceGlobe.tsx:550" className="flex items-center gap-1.5 font-mono text-[10px] sm:text-xs text-muted-foreground">
          <Radio className="size-3 text-cyan-400 animate-pulse" />
          <span data-upbo-src="src/components/SpaceGlobe.tsx:552">MESH {telemetryLatency}MS</span>
        </div>
      </div>

      {/* 3D Canvas Viewport */}
      <div data-upbo-src="src/components/SpaceGlobe.tsx:557"
        className="relative w-full h-[280px] sm:h-[350px] lg:h-[390px] cursor-grab active:cursor-grabbing touch-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <canvas data-upbo-src="src/components/SpaceGlobe.tsx:564" ref={canvasRef} className="w-full h-full block" />

        {/* Floating Orbital Node Selector */}
        <div data-upbo-src="src/components/SpaceGlobe.tsx:567" className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2 bg-background/80 backdrop-blur-md px-3 py-2 rounded-xl border border-sky-500/20 text-xs">
          <div data-upbo-src="src/components/SpaceGlobe.tsx:568" className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <Satellite className="size-3.5 text-cyan-400 shrink-0" />
            {SATELLITES.map((sat) => (
              <button data-upbo-src="src/components/SpaceGlobe.tsx:571"
                key={sat.id}
                onClick={() => setActiveSat(sat)}
                className={cn(
                  'px-2 py-0.5 rounded font-mono text-[10px] sm:text-xs transition-colors shrink-0',
                  activeSat.id === sat.id
                    ? 'bg-cyan-500/25 text-cyan-300 font-semibold border border-cyan-500/40'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {sat.code}
              </button>
            ))}
          </div>
          <div data-upbo-src="src/components/SpaceGlobe.tsx:585" className="hidden sm:flex items-center gap-1 text-[11px] text-muted-foreground font-mono shrink-0">
            <Compass className="size-3" />
            <span data-upbo-src="src/components/SpaceGlobe.tsx:587">DRAG TO ROTATE</span>
          </div>
        </div>
      </div>

      {/* Telemetry Status Bar */}
      <div data-upbo-src="src/components/SpaceGlobe.tsx:593" className="mt-3 pt-3 border-t border-sky-500/15 flex items-center justify-between text-[11px] text-muted-foreground font-mono">
        <div data-upbo-src="src/components/SpaceGlobe.tsx:594" className="flex items-center gap-2">
          <span data-upbo-src="src/components/SpaceGlobe.tsx:595" className="size-1.5 rounded-full bg-emerald-400" />
          <span data-upbo-src="src/components/SpaceGlobe.tsx:596" className="text-foreground/80 font-medium">{activeSat.name}</span>
          <span data-upbo-src="src/components/SpaceGlobe.tsx:597" className="hidden sm:inline text-muted-foreground/60">• {activeSat.altitude}</span>
        </div>
        <span data-upbo-src="src/components/SpaceGlobe.tsx:599" className="text-cyan-400 font-semibold">{activeSat.status}</span>
      </div>
    </div>
  )
}
